import React, { FC, useCallback, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CardSchema, CollectionCardLinkSchema, UserObjectType } from 'constants/graphqlTypes';
import SwiperCore, { Mousewheel } from 'swiper';
import { styled } from 'linaria/react';

import 'swiper/swiper.min.css';
import getStyles from 'helpers/getStyles';
import CardDetails from 'components/CardDetails';

interface IUserPicksImmersiveProps {
  cards: CardSchema[];
  title?: string;
  onBgUpdate?: (bg: string) => void;
  onActiveChange?: (card: CardSchema) => void;
  defaultCreatedAt?: string;
}

const StyledOverlayContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  padding: 0;
`;

const UserPicksImmersive: FC<IUserPicksImmersiveProps> = ({ cards, onBgUpdate, onActiveChange, defaultCreatedAt }) => {
  const getSafeCard = useCallback(
    (card: CardSchema): CardSchema => {
      const collectionLink = (card?.extraData?.items ?? []).find(
        (item): item is CollectionCardLinkSchema => item?.__typename === 'CollectionCardLinkSchema'
      );

      const maybeStampIso =
        typeof collectionLink?.createdAtStamp === 'number'
          ? new Date(collectionLink.createdAtStamp * 1000).toISOString()
          : undefined;

      const defaultIso = defaultCreatedAt && !Number.isNaN(Date.parse(defaultCreatedAt)) ? defaultCreatedAt : undefined;

      const candidateCreatedAt =
        collectionLink?.pickCreatedAt ?? collectionLink?.createdAt ?? maybeStampIso ?? card?.createdAt ?? defaultIso;

      let withDate: CardSchema;
      if (candidateCreatedAt && !Number.isNaN(Date.parse(candidateCreatedAt))) {
        withDate =
          candidateCreatedAt === card?.createdAt
            ? (card as CardSchema)
            : ({ ...card, createdAt: candidateCreatedAt } as CardSchema);
      } else {
        withDate = { ...card, createdAt: new Date().toISOString() } as CardSchema;
      }

      const hasExtra = withDate?.extraData?.items && withDate.extraData.items.length > 0;
      if (hasExtra) return withDate;

      const fallbackUser = withDate?.user || (withDate?.pickedByUsers?.items?.[0] as UserObjectType | undefined);
      if (!fallbackUser) return withDate;

      return {
        ...withDate,
        extraData: {
          __typename: 'UnionExtraDataListObjectType',
          totalCount: 1,
          items: [
            {
              __typename: 'CardPickSchema',
              id: `fallback-${withDate.id}`,
              user: fallbackUser,
            },
          ],
        },
      } as CardSchema;
    },
    [defaultCreatedAt]
  );

  useEffect(() => {
    if (cards && cards.length > 0) {
      const safeCard = getSafeCard(cards[0]);
      if (onBgUpdate) {
        const { gradientStart, bgColor } = getStyles(safeCard.cardStyle);
        onBgUpdate(gradientStart ?? bgColor);
      }
      onActiveChange?.(safeCard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, getSafeCard]);

  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);

  return (
    <StyledOverlayContent>
      <Swiper
        style={{ height: '100%' }}
        speed={600}
        followFinger
        direction="vertical"
        grabCursor
        mousewheel={{ sensitivity: 0.6, thresholdTime: 100, releaseOnEdges: true }}
        modules={[Mousewheel]}
        touchStartPreventDefault={false}
        onSwiper={(s) => setSwiperInstance(s)}
        onSlideChange={(swiper) => {
          const idx = swiper.realIndex;
          const card = cards[idx];
          if (card) {
            const safeCard = getSafeCard(card);
            if (onBgUpdate) {
              const { gradientStart, bgColor } = getStyles(safeCard.cardStyle);
              onBgUpdate(gradientStart ?? bgColor);
            }
            onActiveChange?.(safeCard);
          }
        }}
      >
        {cards?.map((card) => (
          <SwiperSlide key={card.id}>
            <div style={{ minHeight: '100%', display: 'flex' }}>
              <CardDetails
                item={getSafeCard(card)}
                swiperInstance={(swiperInstance as unknown) as SwiperCore | null}
                showAddReaction
                isUserPicksImmersive
                bgColorOverride="transparent"
                hideGrain
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </StyledOverlayContent>
  );
};

export default UserPicksImmersive;
